import "./App.css";
import "react-simple-toasts/dist/style.css"; // Will give a warning, but works anyway.
import "react-simple-toasts/dist/theme/info.css";
import "react-simple-toasts/dist/theme/success.css";
import "react-simple-toasts/dist/theme/failure.css";
import { toastConfig } from "react-simple-toasts";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import Menu from "@mui/material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";

import logo_adn from "./images/logo.jpg";
import ProductsList from "./components/products/List";
import PublicationsList from "./components/publications/List";
import Landing from "./components/landing/Landing";
import AddProduct from "./components/products/Add";
import AddPublication from "./components/publications/Add";
import useAuth, { AuthProvider } from "./hooks/useAuth";
import Profile from "./components/accounts/Profile";
import CreateAccount from "./components/accounts/CreateAccount";
import Login from "./components/accounts/Login";
import Logout from "./components/accounts/Logout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import ReservationsList from "./components/reservations/List";

axios.defaults.withCredentials = true;

// specify the theme in toastConfig
toastConfig({
  theme: "dark",
  zIndex: 9999,
});

const lightTheme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: "#cecece",
      paper: "#ebebeb",
    },
    primary: {
      main: "#2c3e50",
      light: "#34495e",
      dark: "#1a252f",
    },
    secondary: {
      main: "#3498db",
      light: "#5dade2",
      dark: "#2980b9",
    },
    text: {
      primary: "#2c3e50",
      secondary: "#555555",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#1a1f2e", // deep navy, matches your primary dark
      paper: "#222b3a", // slightly lighter for cards/surfaces
    },
    primary: {
      main: "#5dade2", // your secondary light — pops on dark bg
      light: "#85c1e9",
      dark: "#3498db",
    },
    secondary: {
      main: "#3498db",
      light: "#5dade2",
      dark: "#2980b9",
    },
    text: {
      primary: "#ecf0f1", // soft white, easy on the eyes
      secondary: "#95a5a6", // muted gray for secondary text
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  const [isDark, setIsDark] = React.useState(false);

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent isDark={isDark} setIsDark={setIsDark} />
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent({ isDark, setIsDark }) {
  const [products, setProducts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [publications, setPublications] = useState([]);
  const [landingPageContent, setLandingPageContent] = useState([]);
  const [openingHours, setOpeningHours] = useState([]);

  const { user } = useAuth();

  const pages = [
    { name: "Produits", path: "/produits" },
    { name: "Publications", path: "/publications" },
  ];
  if (user) {
    if (user.accountIsAdmin) {
      pages.push({ name: "Ajouter produit", path: "/produits/ajouter" });
      pages.push({
        name: "Ajouter publication",
        path: "/publications/ajouter",
      });
      pages.push({ name: "Réservations", path: "/reservations" });
    } else {
      pages.push({ name: "Mes réservations", path: "/mes-reservations" });
    }
  }

  const settings = user
    ? [
        { name: "Profil", path: "/compte/profil" },
        { name: "Déconnexion", path: "/compte/deconnexion" },
      ]
    : [{ name: "Connexion", path: "/compte/connexion" }];

  const API_URL = process.env.REACT_APP_API_URL;
  // Get all the data we need for the rendering of the pages
  useEffect(() => {
    axios
      .get(`${API_URL}/products/get`)
      .then((res) => setProducts(res.data))
      .catch((err) => console.log(err));

    if (user && user.accountIsAdmin) {
      axios
        .get(`${API_URL}/reservations/getAll`)
        .then((res) => setReservations(res.data))
        .catch((err) => console.log(err));
    } else {
      axios
        .get(`${API_URL}/reservations/get`)
        .then((res) => setReservations(res.data))
        .catch((err) => console.log(err));
    }

    axios
      .get(`${API_URL}/publications/get`)
      .then((res) => setPublications(res.data))
      .catch((err) => console.log(err));

    axios
      .get(`${API_URL}/config/getLandingPageContent`)
      .then((res) => {
        setLandingPageContent(res.data[0].landingPageContent);
        setOpeningHours(res.data[0].openingHours);
      })
      .catch((err) => console.log(err));
  }, [user]);

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <Router>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AppBar position="static">
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <Box
                component={Link}
                to="/"
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  mr: 2,
                }}
              >
                <Box
                  component="img"
                  src={logo_adn}
                  alt="Logo"
                  sx={{ width: "40px", height: "40px" }}
                />
              </Box>
              <Typography
                variant="h6"
                noWrap
                component={Link}
                to="/"
                sx={{
                  mr: 2,
                  display: { xs: "none", md: "flex" },
                  fontFamily: "monospace",
                  fontWeight: 700,
                  letterSpacing: ".3rem",
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                Les Jardins ADN
              </Typography>

              <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{ display: { xs: "block", md: "none" } }}
                >
                  {pages.map((page) => (
                    <MenuItem
                      key={page.name}
                      component={Link}
                      to={page.path}
                      onClick={handleCloseNavMenu}
                    >
                      <Typography sx={{ textAlign: "center" }}>
                        {page.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <Box
                component={Link}
                to="/"
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  mr: 2,
                }}
              >
                <Box
                  component="img"
                  src={logo_adn}
                  alt="Logo"
                  sx={{ width: "40px", height: "40px" }}
                />
              </Box>
              <Typography
                variant="h5"
                noWrap
                component={Link}
                to="/"
                sx={{
                  mr: 2,
                  display: { xs: "flex", md: "none" },
                  flexGrow: 1,
                  fontFamily: "monospace",
                  fontWeight: 700,
                  letterSpacing: ".3rem",
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                Jardins ADN
              </Typography>
              <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                {pages.map((page) => (
                  <Button
                    key={page.name}
                    component={Link}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    sx={{ my: 2, color: "white", display: "block" }}
                  >
                    {page.name}
                  </Button>
                ))}
              </Box>
              <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: "primary.dark" }}>
                      {user ? (
                        user.accountFirstName[0].toUpperCase()
                      ) : (
                        <PersonIcon />
                      )}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map((setting) => (
                    <MenuItem
                      key={setting.name}
                      component={Link}
                      to={setting.path}
                      onClick={handleCloseUserMenu}
                    >
                      <Typography sx={{ textAlign: "center" }}>
                        {setting.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        <Box sx={{ flex: 1 }}>
          <Routes>
            <Route
              path="/"
              element={
                <Landing
                  user={user}
                  landingPageContent={landingPageContent}
                  setLandingPageContent={setLandingPageContent}
                  openingHours={openingHours}
                  setOpeningHours={setOpeningHours}
                />
              }
            />
            <Route
              path="/produits"
              element={
                <ProductsList
                  products={products}
                  setProducts={setProducts}
                  setReservations={setReservations}
                  setAllReservations={setAllReservations}
                  user={user}
                  openingHours={openingHours}
                />
              }
            />
            <Route
              path="/publications"
              element={
                <PublicationsList
                  publications={publications}
                  setPublications={setPublications}
                  user={user}
                />
              }
            />
            <Route
              path="/produits/ajouter"
              element={
                <ProtectedRoute>
                  <AddProduct setProducts={setProducts} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/publications/ajouter"
              element={
                <ProtectedRoute>
                  <AddPublication setPublications={setPublications} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compte/profil"
              element={
                <ProtectedRoute>
                  <Profile account={user} />
                </ProtectedRoute>
              }
            />
            <Route path="/compte/connexion" element={<Login user={user} />} />
            <Route path="/compte/deconnexion" element={<Logout />} />
            <Route
              path="/compte/creer-compte"
              element={<CreateAccount user={user} />}
            />
            <Route
              path="/mes-reservations"
              element={
                <ProtectedRoute>
                  <ReservationsList
                    reservations={reservations}
                    setReservations={setReservations}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reservations"
              element={
                <AdminRoute>
                  <ReservationsList
                    reservations={reservations}
                    setReservations={setReservations}
                    allReservations={allReservations}
                    setAllReservations={setAllReservations}
                    isAdmin={user && user.accountIsAdmin}
                  />
                </AdminRoute>
              }
            />
          </Routes>
        </Box>
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 4,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body1" align="center">
            @2026 Les Jardins ADN. Tous droits réservés.
          </Typography>
        </Box>
      </Box>
    </Router>
  );
}

export default App;
